import React from 'react';

import { Box, Button, Flex, Paper, ScrollArea, Stack, Text, Textarea, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MemoryService } from 'services/memory-service';
import { NotificationService } from 'services/notification-service';
import { MEMORY_PROMPT_MAX_LENGTH, MEMORY_PROMPT_MIN_LENGTH } from 'shared/constants/memory';
import { formatChatMessageToReadable } from 'shared/constants/smart-assistant-chat';
import { Conversation } from 'shared/interfaces/Conversation';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { maxLengthValidator, minLengthValidator, requiredInputValidator } from 'shared/utils/form-validators';

interface CreateMemoryGenerateViewProps {
  conversation?: Conversation;
  smartAssistant: SmartAssistant;
  onMemoryGenerated: (memory: string) => void;
  onCancel: () => void;
}

export function CreateMemoryGenerateView({
  conversation,
  smartAssistant,
  onMemoryGenerated,
  onCancel
}: CreateMemoryGenerateViewProps) {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = React.useState(false);

  const memoryConfigForm = useForm({
    initialValues: {
      memoryPrompt: '',
      memoryCondition: ''
    },
    validate: {
      memoryPrompt: (value) =>
        requiredInputValidator(value) ||
        minLengthValidator(value, MEMORY_PROMPT_MIN_LENGTH) ||
        maxLengthValidator(value, MEMORY_PROMPT_MAX_LENGTH),
      memoryCondition: (value) =>
        // The validation should only be applied if the memory condition is not empty
        value
          ? minLengthValidator(value, MEMORY_PROMPT_MIN_LENGTH) || maxLengthValidator(value, MEMORY_PROMPT_MAX_LENGTH)
          : null
    }
  });

  const generateMemoryMutation = useMutation({
    mutationFn: MemoryService.generateMemory,
    onError: () => {
      NotificationService.showResult(false, 'FEED_BACK.MEMORY.FAILURE.GENERATE');
      setIsGenerating(false);
    }
  });

  const handleGenerateMemory = async () => {
    const smartAssistantId = smartAssistant._id;
    const validation = memoryConfigForm.validate();
    if (!smartAssistantId || validation.hasErrors) return;

    // Create conversation context for current conversation messages. Will be empty if there is no conversation
    const conversationContext = conversation?.messages.map(formatChatMessageToReadable).join('\n');

    setIsGenerating(true);
    const generatedMemory = await generateMemoryMutation.mutateAsync({
      conversationContext: conversationContext || '',
      smartAssistantId,
      memoryPrompt: memoryConfigForm.values.memoryPrompt,
      memoryCondition: memoryConfigForm.values.memoryCondition
    });
    setIsGenerating(false);
    onMemoryGenerated(generatedMemory.memory);
  };

  return (
    <Stack spacing={16}>
      {conversation && (
        <Stack spacing={4}>
          <Title order={4}>{t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.GENERATE_VIEW.CONVERSATION_CONTEXT')}</Title>
          <Paper withBorder>
            <ScrollArea.Autosize mah={200} p="xs">
              {conversation.messages.map((message, index) => (
                <Box key={index}>
                  <Text py={4}>
                    <Text span weight={600}>
                      {formatChatMessageToReadable(message).sourceReadable}:
                    </Text>
                    {` ${formatChatMessageToReadable(message).textReadable}`}
                  </Text>
                </Box>
              ))}
            </ScrollArea.Autosize>
          </Paper>
        </Stack>
      )}

      <Stack spacing={4}>
        <Title order={4}>{t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.GENERATE_VIEW.MEMORY_PROMPT')}</Title>
        <Textarea
          minRows={4}
          maxRows={4}
          placeholder={t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.GENERATE_VIEW.GENERATE_MEMORY_PLACEHOLDER')}
          {...memoryConfigForm.getInputProps('memoryPrompt')}
        />
      </Stack>

      <Stack spacing={4}>
        <Title order={4}>{t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.GENERATE_VIEW.MEMORY_CONDITION')}</Title>
        <Textarea
          minRows={2}
          maxRows={2}
          placeholder={t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.GENERATE_VIEW.MEMORY_CONDITION_PLACEHOLDER')}
          {...memoryConfigForm.getInputProps('memoryCondition')}
        />
      </Stack>

      <Flex justify="flex-end">
        <Flex justify="space-between" gap={16}>
          <Button onClick={onCancel} variant="default">
            {t('APP.ACTIONS.CANCEL')}
          </Button>
          <Button onClick={handleGenerateMemory} loading={isGenerating}>
            {t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.GENERATE_VIEW.GENERATE_MEMORY')}
          </Button>
        </Flex>
      </Flex>
    </Stack>
  );
}
